import React, { useState, useEffect, useContext } from "react";
import { styled } from "@mui/system";
import { useTheme, useMediaQuery, Stack, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GlobalContext } from "store/globalContext";
import { customColors } from "models/customColors";

const DESKTOP_MODAL_WIDTH_IN_PX = 400;
const MOBILE_MODAL_WIDTH_IN_PX = 335;
const MODAL_MARGIN_FROM_WINDOW_IN_PX = 100;
const SCREEN_PADDING_IN_PX = 15;

const Modal = styled("div")`
  position: absolute;
  left: ${({
    // @ts-ignore
    x,
  }) => x}px;
  top: ${({
    // @ts-ignore
    y,
  }) => y}px;
  width: ${({
    // @ts-ignore
    modalWidth,
  }) => modalWidth}px;
  height: ${({
    // @ts-ignore
    height,
  }) => height}px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;

  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  overflow: hidden;
`;

const ModalHeader = styled("div")`
  padding: 10px 40px 10px 20px;
  background-color: #f4f4f4;
  border-bottom: 1px solid #ccc;
  display: flex;
  justify-content: space-between;
  cursor: move;
  user-select: none;

  color: white;
  background-color: ${customColors.black};
`;

const ModalBody = styled(Stack)`
  flex: 1 1 auto;
  justify-content: space-between;
  align-items: stretch;

  height: 100%;
  padding: 0;
  overflow: hidden;
`;

const getScreenWidth = () => {
  const widths = [window.innerWidth];

  if (window.screen?.width) {
    widths.push(window.screen?.width);
  }

  return Math.min(...widths);
};

const getScreenHeight = () => {
  const heights = [window.innerHeight];

  if (window.screen?.height) {
    heights.push(window.screen?.height);
  }

  return Math.min(...heights);
};

const getCenterOffset = (modalHeight, modalWidth) => {
  return {
    x: getScreenWidth() / 2 - modalWidth / 2,
    y: getScreenHeight() / 2 - modalHeight / 2,
  };
};

export const MovableModal = ({ title, isOpen, onClose, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userData } = useContext(GlobalContext);

  const [modalWidth, setModalWidth] = useState(
    isMobile ? MOBILE_MODAL_WIDTH_IN_PX : DESKTOP_MODAL_WIDTH_IN_PX
  );

  const calculateModalHeight = () =>
    getScreenHeight() - MODAL_MARGIN_FROM_WINDOW_IN_PX;

  const defaultPosition =
    window.localStorage.getItem(`${userData.cognitoId}-messages-position`) ??
    null;

  const [modalHeight, setModalHeight] = useState(calculateModalHeight());
  const [position, setPosition] = useState(
    defaultPosition
      ? JSON.parse(defaultPosition)
      : getCenterOffset(calculateModalHeight(), modalWidth)
  );
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const setPersistedPosition = (position) => {
    window.localStorage.setItem(
      `${userData.cognitoId}-messages-position`,
      JSON.stringify(position)
    );
    setPosition(position);
  };

  useEffect(() => {
    if (!isOpen) return;

    const defaultPosition =
      window.localStorage.getItem(`${userData.cognitoId}-messages-position`) ??
      null;
    if (!defaultPosition) return;

    const position = JSON.parse(defaultPosition);
    const width = getScreenWidth();

    if (position.x < SCREEN_PADDING_IN_PX) {
      setPersistedPosition({
        x: SCREEN_PADDING_IN_PX,
        y: position.y,
      });
    } else if (position.x > width - modalWidth - SCREEN_PADDING_IN_PX) {
      const x = width - modalWidth - SCREEN_PADDING_IN_PX;
      setPersistedPosition({
        x,
        y: position.y,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalWidth]);

  useEffect(() => {
    const handleResize = () => {
      const width = getScreenWidth();
      const newModalWidth =
        width <= theme.breakpoints.values.sm
          ? MOBILE_MODAL_WIDTH_IN_PX
          : DESKTOP_MODAL_WIDTH_IN_PX;
      setModalWidth(newModalWidth);
      const newHeight = calculateModalHeight();
      setModalHeight(newHeight);
      const newCenterOffset = getCenterOffset(newHeight, newModalWidth);
      setPersistedPosition(newCenterOffset);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, theme.breakpoints.values.sm]);

  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset((prev) => ({
      ...prev,
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    }));
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      let x = e.clientX - offset.x;
      let y = e.clientY - offset.y;

      if (x < SCREEN_PADDING_IN_PX) {
        x = SCREEN_PADDING_IN_PX;
      } else if (x > window.innerWidth - modalWidth - SCREEN_PADDING_IN_PX) {
        x = window.innerWidth - modalWidth - SCREEN_PADDING_IN_PX;
      }

      if (y < SCREEN_PADDING_IN_PX) {
        y = SCREEN_PADDING_IN_PX;
      } else if (y > window.innerHeight - modalHeight - SCREEN_PADDING_IN_PX) {
        y = window.innerHeight - modalHeight - SCREEN_PADDING_IN_PX;
      }

      setPersistedPosition({
        x,
        y,
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  if (!isOpen) return null;

  return (
    <Modal
      // @ts-ignore
      x={position.x}
      y={position.y}
      modalWidth={modalWidth}
      height={modalHeight}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <Stack
        alignItems={"flex-end"}
        sx={{
          margin: 0,
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="large" sx={{ color: "white" }} />
        </IconButton>
      </Stack>
      <ModalHeader onMouseDown={handleMouseDown}>{title}</ModalHeader>
      <ModalBody>{children}</ModalBody>
    </Modal>
  );
};
